import { styled } from "@mui/material/styles";
import parse, { domToReact, DOMNode, Element } from "html-react-parser";
import type { DomElement } from "htmlparser2";
import { HTMLAttributes, ComponentType } from "react";

import { TextBaseProps, TextBody, TextCaption, TextTitle4, Text, TextTitle3 } from "./Text";

export const Wrapper = styled("div")(({ theme }) => ({
    "& p, h1, h2, h3, h4, h5, h6, ul, ol": {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

const TITLES = new Set(["h2", "h3", "h4", "h5", "h6"]);

function isElement(domNode: DOMNode): domNode is Element {
    const isTag = domNode.type === "tag";
    const hasAttributes = "attribs" in domNode && !!domNode.attribs;

    return isTag && hasAttributes;
}
const buildOptions = (variant: string) => {
    return {
        replace: (node: DomElement) => {
            if (!isElement(node)) return null;
            const options = buildOptions(variant);
            const { name, children, attribs } = node;
            const { href } = attribs;

            const style = { fontSize: "1em" };
            let TextComponent: ComponentType<TextBaseProps> = Text;
            if (variant === "body") TextComponent = TextBody;
            else if (variant === "caption") TextComponent = TextCaption;
            else TextComponent = TextBody;

            if (name === "h1")
                return (
                    <TextTitle3 bold style={style}>
                        {domToReact(children, options)}
                    </TextTitle3>
                );
            else if (TITLES.has(name)) {
                return (
                    <TextTitle4 bold style={style}>
                        {domToReact(children, options)}
                    </TextTitle4>
                );
            } else if (name === "strong")
                return (
                    <TextComponent component="span" style={{ fontWeight: "bold", ...style }}>
                        {domToReact(children, options)}
                    </TextComponent>
                );
            else if (name === "em")
                return (
                    <TextComponent component="span" style={{ fontStyle: "italic", ...style }}>
                        {domToReact(children, options)}
                    </TextComponent>
                );
            else if (name === "u")
                return (
                    <TextComponent
                        component="span"
                        style={{
                            textDecoration: "underline",
                            ...style,
                        }}
                    >
                        {domToReact(children, options)}
                    </TextComponent>
                );
            else if (name === "p")
                return (
                    <TextComponent component="div" paragraph style={style}>
                        {domToReact(children, options)}
                    </TextComponent>
                );
            else if (name === "a")
                return (
                    <TextComponent component="span" href={href} newTab style={style}>
                        {domToReact(children, options)}
                    </TextComponent>
                );
            else if (name === "br") return <br />;
            else if (name === "ul" || name === "ol") {
                return (
                    <TextComponent component={name} style={style}>
                        {domToReact(children, options)}
                    </TextComponent>
                );
            } else if (name === "li") {
                return <li style={style}>{domToReact(children, options)}</li>;
            } else {
                return <div>{domToReact(children, options)}</div>;
            }
        },
    };
};

export type HTMLContentProps = {
    content: string;
    variant?: string;
} & HTMLAttributes<HTMLDivElement>;

export function HTMLContent({ content, variant = "body", ...props }: HTMLContentProps) {
    const options = buildOptions(variant);
    return <Wrapper {...props}>{parse(content, options)}</Wrapper>;
}
